import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PaymentTimeline from '../components/timeline/PaymentTimeline';
import PdfPreviewModal from '../components/pdf/PdfPreviewModal';
import { 
  paymentsApi, 
  PaymentTubeMapResponseModel, 
  PaymentTrackingUISearchResultModel 
} from '../services/api/paymentsApi';

const PaymentDetailPage: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<PaymentTrackingUISearchResultModel | null>(null);
  const [timeline, setTimeline] = useState<PaymentTubeMapResponseModel[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Fetch payment and timeline data
  useEffect(() => {
    const fetchData = async () => {
      if (!paymentId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Search for the payment details
        const searchResult = await paymentsApi.searchPayments({
          aggregateId: paymentId
        });
        
        // If payment found, get its details
        if (searchResult && searchResult.length > 0) {
          const paymentData = searchResult[0];
          setPayment(paymentData);
          
          // Get timeline (tube map)
          const timelineData = await paymentsApi.getPaymentTimeline(
            paymentId, 
            paymentData.direction.toLowerCase() === 'outbound'
          );
          setTimeline(timelineData);
          
          // Get payment summary for PDF
          try {
            const summaryData = await paymentsApi.getPaymentSummary(paymentId);
            setPaymentSummary(summaryData);
          } catch (summaryError) {
            console.error('Error fetching payment summary:', summaryError);
            // Don't set error state here as it's not critical
          }
        } else {
          setError('Payment not found');
        }
      } catch (err) {
        console.error('Error fetching payment details:', err);
        setError('Failed to load payment details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [paymentId]);

  // Mock function to send email
  const sendEmail = () => {
    return new Promise<void>((resolve, reject) => {
      // Simulate API call to email service
      setTimeout(() => {
        console.log('Email sent to client', {
          level: 'info',
          timestamp: new Date().toISOString(),
          action: 'email_payment_proof',
          status: 'success'
        });
        resolve();
      }, 1500);
    });
  };

  // Handle sending email
  const handleSendEmail = async () => {
    try {
      await sendEmail();
      setEmailSent(true);
    } catch (err) {
      console.error('Error sending email:', err);
      alert('Failed to send email. Please try again later.');
    }
  };

  // Go back to dashboard
  const handleBackToDashboard = () => {
    navigate('/');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <button
            onClick={handleBackToDashboard}
            style={{
              background: 'none',
              border: 'none',
              color: '#003366',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 0',
              fontSize: '14px'
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ margin: '16px 0' }}>Payment Details</h1>
          {payment && (
            <div style={{ color: '#666' }}>
              {payment.externalPaymentId}
            </div>
          )}
        </div>
        
        {payment && (
          <button
            onClick={() => setShowPdfModal(true)}
            style={{
              backgroundColor: '#003366',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 16px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Generate PDF
          </button>
        )}
      </div>
      
      {/* Loading State */}
      {isLoading && (
        <div style={{ 
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
        }}>
          Loading payment details...
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div style={{ 
          padding: '16px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
        }}>
          {error}
        </div>
      )}
      
      {/* Payment Summary */}
      {payment && !isLoading && !error && (
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '4px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '24px',
          }}>
            <div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                Status
              </div>
              <div style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 'bold',
                padding: '4px 8px',
                borderRadius: '16px',
                backgroundColor: payment.paymentStatus === 'Succeeded' ? '#e6f7ed' : 
                                  payment.paymentStatus === 'Failed' ? '#ffebee' : '#fff8e1',
                color: payment.paymentStatus === 'Succeeded' ? '#2e7d32' :
                       payment.paymentStatus === 'Failed' ? '#c62828' : '#ef6c00',
              }}>
                <div style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  backgroundColor: payment.paymentStatus === 'Succeeded' ? '#4caf50' :
                                    payment.paymentStatus === 'Failed' ? '#f44336' : '#ff9800'
                }} />
                {payment.paymentStatus}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                Method
              </div>
              <div style={{ fontWeight: 'bold' }}>{payment.paymentMethod}</div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                From
              </div>
              <div style={{ fontWeight: 'bold' }}>
                {payment.fromCurrency ? (
                  new Intl.NumberFormat('en-GB', { 
                    style: 'currency', 
                    currency: payment.fromCurrency,
                  }).format(payment.fromAmount)
                ) : '-'}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                To
              </div>
              <div style={{ fontWeight: 'bold' }}>
                {payment.toCurrency ? (
                  new Intl.NumberFormat('en-GB', { 
                    style: 'currency', 
                    currency: payment.toCurrency,
                  }).format(payment.toAmount)
                ) : '-'}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                Business Area
              </div>
              <div>{payment.businessArea}</div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                Start Time
              </div>
              <div>
                {new Date(payment.startTime).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Timeline */}
      {!isLoading && !error && timeline.length > 0 && paymentId && (
        <PaymentTimeline data={timeline} paymentId={paymentId} />
      )}
      
      {/* PDF Preview Modal */}
      {showPdfModal && payment && (
        <PdfPreviewModal
          payment={payment}
          paymentSummary={paymentSummary}
          onClose={() => setShowPdfModal(false)}
          onSendEmail={handleSendEmail}
        />
      )}
    </div>
  );
};

export default PaymentDetailPage; 